import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { ME_KEY } from '../auth/session';
import { RealtimeClient } from './realtime-client';

const RealtimeContext = createContext<RealtimeClient | null>(null);

/**
 * Подключение realtime для вошедшего пользователя: одно на вкладку, закрывается при выходе
 * из закрытой части приложения. Отказ на handshake — повод перепроверить сессию.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [client] = useState(
    () =>
      new RealtimeClient({
        onSessionLost: () => void queryClient.invalidateQueries({ queryKey: ME_KEY }),
      }),
  );
  useEffect(() => () => client.close(), [client]);
  return <RealtimeContext.Provider value={client}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeClient {
  const client = useContext(RealtimeContext);
  if (client === null) throw new Error('useRealtime outside RealtimeProvider');
  return client;
}

/** Есть ли сейчас подключение (индикатор «офлайн»). */
export function useRealtimeConnected(): boolean {
  const client = useRealtime();
  const [connected, setConnected] = useState(client.connected);
  useEffect(() => client.onStatus(setConnected), [client]);
  return connected;
}
